/**
 * EncoreFormRenderer schema definitions for the profile page edit mode.
 *
 * The keys are the normalised profile paths, not any entity's field names — which is what lets one
 * form serve both customer profiles. Which fields render, and which three are read-only, stays page
 * design: the server enforces its own writable allowlist but deliberately publishes no field list
 * (design §5.2), because the update action's own form is the admin CRUD form and rendering it here
 * would both expose admin fields and hide the timezone control this page exists for.
 * Uses 16-column grid layout with equal 2-column split (columns: 8 each).
 * Labels are localized via the provided translate function.
 */

import type { TranslateFunction } from '@locus-taxy/encore-i18n-toolkit';
import { getGenderOptions } from './profileConstants';

type TranslateFn = TranslateFunction;

/**
 * Builds the personal info schema with localized labels.
 *
 * @param genderValues the person entity's gender values, as the backend serves them
 */
export function buildPersonalInfoSchema(
  t: TranslateFn,
  genderValues: readonly string[]
) {
  return {
    options: { showNavigation: false },
    components: [
      {
        type: 'textfield',
        id: 'fullName',
        key: 'identity.displayName',
        label: t('dashboard.userDetails.fullName', 'Full Name'),
        disabled: true,
        layout: { row: 'Row_1', columns: 8 },
      },
      {
        type: 'textfield',
        id: 'emailAddress',
        key: 'contact.email',
        label: t('dashboard.userDetails.emailAddress', 'Email Address'),
        disabled: true,
        layout: { row: 'Row_1', columns: 8 },
      },
      {
        type: 'textfield',
        id: 'phoneNumber',
        key: 'contact.phone',
        label: t('dashboard.userDetails.phoneNumber', 'Phone Number'),
        layout: { row: 'Row_2', columns: 8 },
        validate: { pattern: '^\\+?[1-9]\\d{1,19}$', maxLength: 20 },
      },
      {
        type: 'textfield',
        id: 'employeeId',
        key: 'identity.employeeId',
        label: t('dashboard.userDetails.employeeId', 'Employee ID'),
        disabled: true,
        layout: { row: 'Row_2', columns: 8 },
      },
      {
        type: 'select',
        id: 'gender',
        key: 'personal.gender',
        label: t('dashboard.userDetails.gender', 'Gender'),
        layout: { row: 'Row_3', columns: 8 },
        values: getGenderOptions(t, genderValues),
      },
      {
        type: 'datetime',
        subtype: 'date',
        id: 'dateOfBirth',
        key: 'personal.dateOfBirth',
        label: t('dashboard.userDetails.dateOfBirth', 'Date of Birth'),
        layout: { row: 'Row_3', columns: 8 },
      },
    ],
  };
}

export interface LocaleSettingsSchemaOptions {
  timezoneOptions: Array<{ label: string; value: string }>;
  localeOptions: Array<{ label: string; value: string }>;
}

/**
 * Builds the locale settings schema with dynamic timezone and locale options
 * derived from the entity schema metadata, and localized labels.
 */
export function buildLocaleSettingsSchema(
  options: LocaleSettingsSchemaOptions,
  t: TranslateFn = (_, d) => d
) {
  return {
    options: { showNavigation: false },
    components: [
      {
        type: 'select',
        id: 'timezone',
        key: 'preferences.timezone',
        label: t('dashboard.userDetails.timezone', 'Timezone'),
        layout: { row: 'Row_1', columns: 8 },
        values: options.timezoneOptions,
      },
      {
        type: 'select',
        id: 'language',
        key: 'preferences.localeIdentifier',
        label: t('dashboard.userDetails.language', 'Language'),
        layout: { row: 'Row_1', columns: 8 },
        values: options.localeOptions,
      },
    ],
  };
}
